import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { User, Moon, Sun, LogOut, WalletCards, TrendingUp, ArrowDownCircle, Save, Loader2 } from 'lucide-react';
import useStore from '@/store/useStore';
import { apiGetWallet, apiGetTransactions, apiGuestUpdateProfile } from '@/services/api';
import RestaurantSwitcher from '@/components/guest/RestaurantSwitcher';

export default function ProfilePage() {
  const {
    guestUser,
    guestTheme,
    wallet,
    transactions,
    toggleGuestTheme,
    guestLogout,
    setGuestUser,
    setWallet,
    setTransactions,
  } = useStore();
  const [profileName, setProfileName] = useState('');
  const [profilePhone, setProfilePhone] = useState('');
  const [profileDob, setProfileDob] = useState('');
  const [profileAnniversary, setProfileAnniversary] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileError, setProfileError] = useState('');
  const [profileSuccess, setProfileSuccess] = useState('');
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const maxDate = useMemo(() => new Date().toISOString().slice(0, 10), []);

  useEffect(() => {
    if (!wallet) {
      apiGetWallet().then(setWallet).catch(() => {});
    }
    if (!transactions?.length) {
      apiGetTransactions().then(setTransactions).catch(() => {});
    }
  }, [wallet, transactions?.length, setTransactions, setWallet]);

  useEffect(() => {
    setProfileName(guestUser?.name || '');
    setProfilePhone(guestUser?.phone || '');
    setProfileDob(guestUser?.date_of_birth || '');
    setProfileAnniversary(guestUser?.anniversary_date || '');
  }, [guestUser]);

  const summary = useMemo(() => {
    const txs = transactions || [];
    const earned = txs.filter((t) => t.points > 0).reduce((acc, t) => acc + t.points, 0);
    const redeemed = txs.filter((t) => t.points < 0).reduce((acc, t) => acc + Math.abs(t.points), 0);
    return { earned, redeemed };
  }, [transactions]);

  const handleLogout = () => {
    setShowLogoutConfirm(true);
  };

  const confirmLogout = () => {
    setShowLogoutConfirm(false);
    guestLogout();
    window.localStorage.clear();
    window.sessionStorage.clear();
    window.location.href = '/store';
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    if (!profileName.trim()) {
      setProfileError('Name is required.');
      setProfileSuccess('');
      return;
    }
    if (profilePhone.trim().length < 10) {
      setProfileError('Please enter a valid phone number.');
      setProfileSuccess('');
      return;
    }

    setSavingProfile(true);
    setProfileError('');
    setProfileSuccess('');
    try {
      const res = await apiGuestUpdateProfile({
        name: profileName.trim(),
        phone: profilePhone.trim(),
        date_of_birth: profileDob || null,
        anniversary_date: profileAnniversary || null,
      });
      if (res?.customer) {
        setGuestUser(res.customer);
      }
      setProfileSuccess('Profile updated successfully.');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setProfileError(typeof detail === 'string' ? detail : 'Unable to update profile right now.');
    } finally {
      setSavingProfile(false);
    }
  };

  return (
    <div className="px-4 pt-10 pb-6 space-y-6" data-testid="profile-page">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
        <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }}>Profile</h1>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="rounded-2xl border border-border bg-card p-5 flex items-center gap-4"
        data-testid="profile-user-card"
      >
        <div className="w-14 h-14 rounded-2xl bg-brand-soft flex items-center justify-center">
          <User className="w-6 h-6 text-brand" />
        </div>
        <div className="flex-1">
          <p className="font-semibold text-base">{guestUser?.name || 'Guest'}</p>
          <p className="text-sm text-muted-foreground">{guestUser?.phone || ''}</p>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.11 }}
        className="rounded-2xl border border-border bg-card p-4 space-y-2"
        data-testid="profile-restaurant-switcher-card"
      >
        <p className="text-sm font-semibold">Select Restaurant</p>
        <RestaurantSwitcher inline className="w-full" />
      </motion.div>

      <motion.form
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.12 }}
        onSubmit={handleSaveProfile}
        className="rounded-2xl border border-border bg-card p-5 space-y-3"
        data-testid="guest-profile-settings"
      >
        <h2 className="text-sm font-semibold">Profile Settings</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <label className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Name</label>
            <Input
              value={profileName}
              onChange={(e) => {
                setProfileName(e.target.value);
                if (profileError) setProfileError('');
              }}
              placeholder="Your name"
              className="h-10 rounded-xl"
              data-testid="guest-profile-name-input"
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Phone</label>
            <Input
              value={profilePhone}
              onChange={(e) => {
                setProfilePhone(e.target.value);
                if (profileError) setProfileError('');
              }}
              placeholder="Phone number"
              className="h-10 rounded-xl"
              data-testid="guest-profile-phone-input"
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Date of Birth (Optional)</label>
            <Input
              type="date"
              value={profileDob}
              onChange={(e) => {
                setProfileDob(e.target.value);
                if (profileError) setProfileError('');
              }}
              max={maxDate}
              className="h-10 rounded-xl"
              data-testid="guest-profile-dob-input"
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Anniversary (Optional)</label>
            <Input
              type="date"
              value={profileAnniversary}
              onChange={(e) => {
                setProfileAnniversary(e.target.value);
                if (profileError) setProfileError('');
              }}
              max={maxDate}
              className="h-10 rounded-xl"
              data-testid="guest-profile-anniversary-input"
            />
          </div>
        </div>
        {profileError && <p className="text-xs text-destructive" data-testid="guest-profile-error">{profileError}</p>}
        {profileSuccess && <p className="text-xs text-emerald-600" data-testid="guest-profile-success">{profileSuccess}</p>}
        <button
          type="submit"
          disabled={savingProfile}
          className="h-10 px-4 rounded-xl gradient-brand-btn text-sm font-semibold flex items-center gap-2 disabled:opacity-50"
          data-testid="guest-profile-save-btn"
        >
          {savingProfile ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save Changes
        </button>
      </motion.form>

      <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="grid grid-cols-3 gap-2">
        <div className="rounded-xl border border-border bg-card p-3">
          <p className="text-[11px] text-muted-foreground">Balance</p>
          <p className="text-sm font-bold flex items-center gap-1 mt-1"><WalletCards className="w-3.5 h-3.5" /> {(wallet?.points || 0).toLocaleString()}</p>
        </div>
        <div className="rounded-xl border border-border bg-card p-3">
          <p className="text-[11px] text-muted-foreground">Earned</p>
          <p className="text-sm font-bold flex items-center gap-1 mt-1 text-emerald-600"><TrendingUp className="w-3.5 h-3.5" /> {summary.earned.toLocaleString()}</p>
        </div>
        <div className="rounded-xl border border-border bg-card p-3">
          <p className="text-[11px] text-muted-foreground">Redeemed</p>
          <p className="text-sm font-bold flex items-center gap-1 mt-1 text-red-500"><ArrowDownCircle className="w-3.5 h-3.5" /> {summary.redeemed.toLocaleString()}</p>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="rounded-2xl border border-border bg-card p-4 flex items-center justify-between"
        data-testid="theme-toggle-row"
      >
        <div>
          <p className="text-sm font-semibold">Dark Mode</p>
          <p className="text-xs text-muted-foreground">Tap to switch appearance</p>
        </div>
        <button
          type="button"
          onClick={toggleGuestTheme}
          className="h-9 w-9 rounded-full border border-brand/30 bg-brand-soft flex items-center justify-center text-brand hover:opacity-90 transition-opacity"
          data-testid="theme-toggle-btn"
          aria-label={guestTheme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
        >
          {guestTheme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
        </button>
      </motion.div>

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}>
        <button
          data-testid="logout-btn"
          onClick={handleLogout}
          className="w-full h-11 rounded-xl font-semibold gradient-brand-btn text-white transition-colors flex items-center justify-center gap-2"
        >
          <LogOut className="w-4 h-4" /> Log Out
        </button>
      </motion.div>

      <Dialog open={showLogoutConfirm} onOpenChange={setShowLogoutConfirm}>
        <DialogContent className="max-w-sm rounded-2xl">
          <DialogTitle className="text-lg font-bold" style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }}>
            Confirm Logout
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            You are about to log out from your account. You can sign back in anytime.
          </DialogDescription>
          <div className="grid grid-cols-2 gap-2 mt-2">
            <button
              type="button"
              onClick={() => setShowLogoutConfirm(false)}
              className="h-10 rounded-xl border border-border text-sm font-semibold hover:bg-secondary"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={confirmLogout}
              className="h-10 rounded-xl gradient-brand-btn text-white text-sm font-semibold"
              data-testid="guest-confirm-logout-btn"
            >
              Log Out
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
